import { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import Button from "common/core/button";
import IllustrationModal from "common/modals/illustration_modal";
import MeetingCompleteImg from "assets/images/meeting/meeting_complete.svg";
import MeetingIncompleteImg from "assets/images/meeting/meeting_incomplete.svg";
import DigicertLogo from "assets/images/digicert/digicert_logo.svg";
import { Requirement } from "common/meeting/notary/requirements";
import ProgressBar from "common/core/progress_bar";
import {
  MeetingOTPCodeForm,
  DIGICERT_OTP_TITLE_COPY,
} from "common/notary/certificate/pending/digicert_otp";
import { useQuery } from "util/graphql";
import { DigicertSigningStatus, MeetingEndedState } from "graphql_globals";

import DocumentBundleHashingStatus from "./document_bundle_hashing_status_query.graphql";
import Styles from "./digicert_otp_modal.module.scss";
import { useNotaryMeetingContext } from "./context";
import { constructUiReqs } from "./header/complete";
import type { NotaryCompleteMeeting as Meeting } from "./header/complete_fragment.graphql";

type ModalProps = {
  onClose: () => void;
  onCompleteMeeting: () => Promise<unknown>;
  onPostCompleteMeeting: (meetingId: string) => void;
  meeting: Meeting;
  numOfUnConfirmedInstructions: number;
};

type DigiCertSigningState =
  | { type: "none" } // meeting hasn't been terminated
  | { type: "loading" } // actively refreshing digicertSigningStatus until confirmed or error
  | { type: "confirm" } // digicertSigningStatus is ready, user needs to submit their otp
  | { type: "signingStatusError" }; // digicertSigningStatus is an error

const MISSING_REQ_TITLE = (
  <FormattedMessage
    id="8243f070-78a3-4c60-9158-b784b21a0a5b"
    defaultMessage="Hm, looks like you're missing requirements..."
  />
);

function getTitle({ type }: DigiCertSigningState) {
  switch (type) {
    case "none":
      return (
        <FormattedMessage
          id="f8718836-4ffe-41ab-80e0-a05bd60dc437"
          defaultMessage="Ready to end this call?"
        />
      );
    case "loading":
      return (
        <FormattedMessage
          id="2d18afec-0e46-47e6-8005-8ce962e186de"
          defaultMessage="Processing documents..."
        />
      );
    case "confirm":
      return DIGICERT_OTP_TITLE_COPY;
    case "signingStatusError":
      return (
        <FormattedMessage
          id="f774bc9e-3e20-43e8-8cf0-17755be55bd1"
          defaultMessage="We’re currently unable to process these documents"
        />
      );
  }
}

const getImage = (digicertStatus: DigiCertSigningState) => {
  switch (digicertStatus.type) {
    case "confirm":
      return DigicertLogo;
    case "signingStatusError":
      return MeetingIncompleteImg;
    case "loading":
    case "none":
      return MeetingCompleteImg;
  }
};

export function DigicertCompleteMeetingModal({
  meeting,
  onClose,
  onCompleteMeeting,
  onPostCompleteMeeting,
  numOfUnConfirmedInstructions,
}: ModalProps) {
  const { id: meetingId, unfulfilledRequirements } = meeting;
  const { analytics, simulatedNotaryProfile } = useNotaryMeetingContext();
  const [digicertStatus, setDigicertStatus] = useState<DigiCertSigningState>({ type: "none" });

  const navigate = useNavigate();
  const { data, stopPolling, startPolling } = useQuery(DocumentBundleHashingStatus, {
    variables: { meetingId },
  });

  useEffect(() => {
    if (
      data?.meeting?.__typename !== "Meeting" ||
      digicertStatus.type === "none" ||
      data.meeting.endedState === MeetingEndedState.NOT_COMPLETED
    ) {
      return;
    }

    const { hashingStatus } = data.meeting.documentBundle!;
    if (hashingStatus === DigicertSigningStatus.PROCESSING) {
      return;
    } else if (hashingStatus === DigicertSigningStatus.READY) {
      setDigicertStatus({ type: "confirm" });
    } else {
      setDigicertStatus({ type: "signingStatusError" });
    }

    stopPolling();
  }, [data?.meeting]);

  const requirements = constructUiReqs(unfulfilledRequirements, numOfUnConfirmedInstructions);
  const missingReq = Boolean(requirements.length);

  const handleComplete = () => {
    return onCompleteMeeting().then(() => {
      if (!simulatedNotaryProfile) {
        analytics.onCompleteMeeting(missingReq);
      }
      setDigicertStatus({ type: "loading" });
      startPolling(1000);
    });
  };

  const getButtons = () => {
    switch (digicertStatus.type) {
      case "none":
        return [
          <Button
            key="0"
            buttonColor={missingReq ? "danger" : "action"}
            variant="primary"
            automationId="complete-button"
            onClick={handleComplete}
          >
            <FormattedMessage
              id="7e2a4337-aadf-4665-ac69-d8ed031c2645"
              defaultMessage="Yes, End Call"
            />
          </Button>,
          <Button
            key="1"
            variant="primary"
            buttonColor="action"
            automationId="return-to-meeting"
            onClick={onClose}
          >
            <FormattedMessage
              id="5e709af6-39ea-4806-b43c-dce2fcc57f32"
              defaultMessage="No, Return to Meeting"
            />
          </Button>,
        ];

      case "signingStatusError":
        return [
          <Button
            key="0"
            buttonColor="action"
            variant="primary"
            onClick={() => {
              navigate("/");
            }}
          >
            <FormattedMessage
              id="2cb6370d-483c-43dd-a455-6402fb153856"
              defaultMessage="Return to dashboard"
            />
          </Button>,
        ];
      case "loading":
      case "confirm":
        return [];
    }
  };

  return (
    <IllustrationModal
      className="NotaryMeetingCompleteModal"
      title={missingReq ? MISSING_REQ_TITLE : getTitle(digicertStatus)}
      src={getImage(digicertStatus)}
      buttons={getButtons()}
    >
      {digicertStatus.type === "none" ? (
        <>
          {!missingReq && (
            <FormattedMessage
              id="acd3746a-d616-408a-a63e-5590440393c4"
              defaultMessage="All meeting requirements have been fulfilled!"
              tagName="p"
            />
          )}
          <div>
            {requirements.map(({ key, value }) => (
              <Requirement key={key} value={value} messageKey={key} />
            ))}
          </div>
        </>
      ) : digicertStatus.type === "loading" ? (
        <>
          <FormattedMessage
            id="088ce2b7-fbc5-49ba-a9b1-1aa8798132a4"
            defaultMessage="Do not close this window."
            tagName="p"
          />
          <FormattedMessage
            id="39bf4b40-cb98-4a27-8e79-531fc822e1f8"
            defaultMessage="This should only take a few moments."
            tagName="p"
          />
          <div className={Styles.progressBar}>
            <ProgressBar barClassName={Styles.progressBarAnimation} />
          </div>
        </>
      ) : digicertStatus.type === "confirm" ? (
        <MeetingOTPCodeForm meetingId={meetingId} onSuccess={onPostCompleteMeeting} />
      ) : (
        <FormattedMessage
          id="221cfd98-9b3c-4111-8972-b5def1f07bb4"
          defaultMessage="Please try again later."
          tagName="p"
        />
      )}
    </IllustrationModal>
  );
}
